import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { MRLocationHistory } from '@pn/core/domain/intel/mr-location-history';
import { isEmpty, isNil, lowerCase, orderBy, startCase } from 'lodash-es';
import React from 'react';
import { InternalLink } from 'src/ui/components/InternalLink';
import { companiesURL } from 'src/ui/pages';

export function LocationHistory({ history }: { history: MRLocationHistory[] }) {
  if (!history || isEmpty(history)) {
    return <Box>No history</Box>;
  }

  const orderedAndFilteredHistory = orderBy(
    history,
    ['status', 'issueDate'],
    ['asc', 'asc']
  ).filter((h) => {
    if (
      h.agreementType.includes('MINERAL') ||
      h.agreementType.includes('ROCK')
    ) {
      return false;
    } else if (
      h.agreementType.includes('PETROLEUM') ||
      h.agreementType.includes('GAS') ||
      h.agreementType.includes('OS')
    ) {
      return true;
    } else if (h.status !== 'POSTED') {
      return false;
    } else {
      return false;
    }
  });

  return (
    <Box>
      {orderedAndFilteredHistory.map((h) => (
        <Agreement key={h.agreementNumber} history={h} />
      ))}
      <Typography sx={{ mt: 4 }} variant="caption" color="textSecondary">
        StackDX only provides participant history as far back as 2022
      </Typography>
    </Box>
  );
}

function Agreement({ history }: { history: MRLocationHistory }) {
  const isActive = history.status.toLowerCase() === 'active';

  const filteredParticipants = React.useMemo(() => {
    const reducedParticipants: MRLocationHistory['participants'] = [];
    let lastParticipantAdded: MRLocationHistory['participants'][0] | null;
    orderBy(history.participants, 'createdAt', ['asc']).forEach(
      (participant) => {
        if (
          !lastParticipantAdded ||
          lastParticipantAdded.name !== participant.name ||
          lastParticipantAdded.companyBrandId !== participant.companyBrandId
        ) {
          reducedParticipants.push(participant);
          lastParticipantAdded = participant;
        }
      }
    );

    return orderBy(reducedParticipants, 'createdAt', ['desc']);
  }, [history.participants]);

  const mostRecentParticipant = orderBy(
    filteredParticipants,
    ['createdAt'],
    ['desc']
  )[0];

  const mostRecentParticipantDate = mostRecentParticipant?.createdAt || '';

  return (
    <Accordion>
      <AccordionSummary
        aria-controls="panel1-content"
        id="panel1-header"
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography
          component="span"
          title={history.status + ': ' + history.agreementType}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {isActive ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}{' '}
          {startCase(lowerCase(history.agreementType))}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>Agreement #</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Area (acres)</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">Rent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{history.agreementNumber}</TableCell>
                <TableCell>{history.issueDate}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isActive ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                    {` ${startCase(lowerCase(history.status))}`}
                  </Box>
                </TableCell>
                <TableCell align="right">{history.area}</TableCell>
                <TableCell align="right">
                  {history.bonus.toLocaleString('en-CA', {
                    style: 'currency',
                    currency: 'CAD',
                  })}
                </TableCell>
                <TableCell align="right">
                  {history.rentCad.toLocaleString('en-CA', {
                    style: 'currency',
                    currency: 'CAD',
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          <Typography sx={{ m: 2 }} variant="subtitle2">
            Zones
          </Typography>
          <TableContainer>
            <Table size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell>Tract</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.zones.map((item) => (
                  <TableRow
                    key={item.tract}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.tract}
                    </TableCell>

                    <TableCell>{item.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Typography sx={{ m: 2 }} variant="subtitle2">
          Participant History
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParticipants.map((item, index) => (
                <TableRow
                  key={item.name + index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {isNil(item.companyBrandId) ? (
                      <>{item.name}</>
                    ) : (
                      <InternalLink
                        to={companiesURL + '/' + item.companyBrandId}
                      >
                        {item.name}
                      </InternalLink>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {item.createdAt}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {item.createdAt != mostRecentParticipantDate
                      ? filteredParticipants[index - 1]?.createdAt
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    {(item.ownershipPercentage / 100).toLocaleString('en-CA', {
                      style: 'percent',
                      minimumFractionDigits: 1,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}
